/**
 * KB-META
 * Purpose: Auth interceptor
 * Role: ui.service
 * EntryPoints: constructor(), if(), pipe(), addToken()
 * Inputs: private router:Router; private sessionService:SessionService; private authService:AuthService; error instanceof HttpErrorResponse && error.status === 401:unknown; switchMap(newToken => {
            this.isRefreshing = false;
            if (newToken:unknown; request:HttpRequest; token:string
 * Outputs: 
 * Validation: 
 * Dependencies: Router, SessionService, AuthService
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=guard/auth.interceptor; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../core/security/token.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ENV as environment } from '../config/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private lastTokenCheck: number = Date.now();
  private readonly TOKEN_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds
  private isRefreshing = false;
  private jwtHelper: JwtHelperService = new JwtHelperService();

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private authService: AuthService,
    private readonly tokenService: TokenService
  ) {}

  private debug(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Dev-only bypass: avoid browser CORS for masterquestion on localhost
    try {
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      if (isLocal && request.url.includes('/v4/masterquestion') && request.method === 'GET') {
        return of(new HttpResponse({ status: 200, body: [] }));
      }
    } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

    // Inject access token into specific endpoints requiring explicit field augmentation
    if (request.url.includes('/partner/admin/reset-password')) {
      const accessToken = this.sessionService.getItem('accessToken') || this.getCookie('accessToken');
      if (accessToken && request.method === 'POST') {
        const existingBody = request.body && typeof request.body === 'object' ? request.body : {};
        // Always enforce freshest accessToken for this endpoint
        request = request.clone({
          setHeaders: {
            // Ensure backend can also read token via Authorization if required
            Authorization: `Bearer ${accessToken}`
          },
          body: {
            ...existingBody,
            accessToken
          }
        });
      }
    }

    // Define endpoints that do NOT require an Authorization header
    const noAuthNeededEndpoints = [
      '/partner/login',
      '/partner/refresh-token',
      '/partner/register',
      '/partner/forgot-password',
      '/partner/confirm-password',
      '/partner/set-new-password',
      '/partner/verify-mfa',
      'https://api.genusconnect.com/v1/moments/moment', 
      'https://www.primefaces.org/cdn/api/upload.php',
      'https://api.openai.com/v1/images/generations',
      'https://noembed.com/embed',
      'https://intouch-mobile.s3.amazonaws.com',
      'https://genus-assets.s3.us-east-1.amazonaws.com',
      'https://stream.genusconnect.org:5443/WebRTCAppEE/',
      'https://97wv2lt7wl.execute-api.us-east-1.amazonaws.com/',
      'https://cors-anywhere.herokuapp.com'
    ];

    const sessionToken = this.sessionService.getItem('token');
    const cookieToken = this.getCookie('token');
    // Ensure encryption key is available as early as possible after refresh
    const encCookie = this.getCookie('encKey');
    if (encCookie) {
      try {
        // Try unwrap from cookie using refresh token
        const rt = this.getCookie('refreshToken');
        const anyWin: any = window as any;
        const injector = anyWin.ng?.getInjector?.();
        const crypto = injector?.get?.((injector as any).get?.constructor?.prototype?.CryptoService) || injector?.get?.(injector?.get?.prototype?.CryptoService);
        if (crypto && typeof crypto.unwrapKeyFromCookie === 'function' && !crypto.isInitialized()) {
          crypto.unwrapKeyFromCookie(encCookie, rt);
        }
      } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    }
    const tokens = sessionToken || cookieToken;
    // Check if request URL matches any no-auth-needed endpoints
    let requiresNoAuth = noAuthNeededEndpoints.some(url => request.url.includes(url));

    // RAG API behavior:
    // - If the request uses x-api-key / x-admin-key, do NOT inject the portal token (avoid mixing auth schemes + avoid CORS preflight issues).
    const hasRagKey = request.headers.has('x-api-key') || request.headers.has('x-admin-key');
    if (hasRagKey) {
      requiresNoAuth = true;
    }

    // console.log(`[AuthInterceptor] Processing request to: ${request.url}`);
    // console.log(`[AuthInterceptor] Requires auth: ${!requiresNoAuth}`);
    // console.log(`[AuthInterceptor] Token exists: ${!!tokens}`);

    // If a token exists and the endpoint does not require skipping the token, add Authorization
    if (tokens && !requiresNoAuth) {
      const currentTime = Date.now();
      const timeSinceLastCheck = currentTime - this.lastTokenCheck;
      const isTokenExpired = this.jwtHelper.isTokenExpired(tokens);

      // console.log(`[AuthInterceptor] Token expired: ${isTokenExpired}`);
      // console.log(`[AuthInterceptor] Time since last check: ${timeSinceLastCheck}ms`, this.TOKEN_CHECK_INTERVAL);
    
      // If token is expired, log out the user
      if (isTokenExpired) {
        // console.log('[AuthInterceptor] Token expired, logging out user');
        this.sessionService.clear();
        this.router.navigate(['/login'], { queryParams: { reason: 'expired' } });
        return throwError(() => new Error('Token expired'));
      }

      // If it's been more than 15 minutes since last check, refresh the token
      if (timeSinceLastCheck >= this.TOKEN_CHECK_INTERVAL && !this.isRefreshing) {
        // console.log('[AuthInterceptor] Refreshing token');
        this.isRefreshing = true;
        this.lastTokenCheck = currentTime;

        return this.authService.refreshToken().pipe(
          switchMap(newToken => {
            this.isRefreshing = false;
            if (newToken) {
              this.sessionService.setItem('token', newToken);
              this.tokenService.set(newToken);
              // Extend encKey cookie to match new token expiry window if present
              try {
                const encKey = this.getCookie('encKey');
                if (encKey) {
                  const helperLocal = new JwtHelperService();
                  const decoded: any = helperLocal.decodeToken(newToken);
                  const expMs = decoded?.exp ? decoded.exp * 1000 : Date.now() + this.TOKEN_CHECK_INTERVAL;
                  const deltaDays = Math.max((expMs - Date.now()) / 864e5, 0.01);
                  const secureAttr = (typeof location !== 'undefined' && location.protocol === 'https:') ? '; Secure' : '';
                  const expires = new Date(Date.now() + deltaDays * 864e5).toUTCString();
                  // Keep same wrapped value; update expiry only
                  document.cookie = `encKey=${encKey}; expires=${expires}; path=/; SameSite=Strict${secureAttr}`;
                }
              } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
              // console.log('[AuthInterceptor] Token refreshed successfully');
              // Retry the original request with the new token
              const newRequest = this.addToken(request, newToken);
              return next.handle(newRequest);
            } else {
              // console.log('[AuthInterceptor] Failed to refresh token');
              this.sessionService.clear();
              this.router.navigate(['/login'], { queryParams: { reason: 'refresh_failed' } });
              return throwError(() => new Error('Failed to refresh token'));
            }
          }),
          catchError(error => {
            this.isRefreshing = false;
            // console.error('[AuthInterceptor] Error refreshing token:', error);
            this.sessionService.clear();

            this.router.navigate(['/login'], { queryParams: { reason: 'refresh_error' } });
            return throwError(() => new Error('Error refreshing token'));
          })
        );
      }

      // Only add Authorization header if one hasn't already been provided (e.g., special-case overrides)
      if (!request.headers.has('Authorization')) {
        request = this.addToken(request, tokens);
        this.debug('[AuthInterceptor] Added token to request');
      } else {
        this.debug('[AuthInterceptor] Authorization header already present; not overriding.');
      }
    }else
    {
      this.debug('[AuthInterceptor] token not added to request');
    }

    return next.handle(request).pipe(
      catchError((error) => {
        this.debug('[AuthInterceptor] Caught error:', error);
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.debug('[AuthInterceptor] Handling 401 error');
          return this.handle401Error(request, next);
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: token,
      },
    });
  }

  private getCookie(name: string): string | null {
    try {
      const cookies = document.cookie.split('; ');
      const cookie = cookies.find(row => row.startsWith(name + '='));
      return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
    } catch {
      return null;
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    this.debug('[AuthInterceptor] Clearing session and redirecting to login');
    this.sessionService.clear();
    this.router.navigate(['/login'], { queryParams: { reason: 'unauthorized' } });
    return throwError(() => new Error('Unauthorized'));
  }
}
